/**
 * useShellTurnTracker - the turn tracker, as the shell wires it.
 *
 * Composes `useTurnTracker` with the table's initiative rulings, and adds
 * what the shell owns around it:
 *
 * - START-OF-TURN EFFECT PROMPTS (v7.7.0): a local queue - a combatant can
 *   be subject to two at once, and nobody else resolves these.
 * - `sheetForCombatant(id)`: the Character behind a combatant, read through
 *   a ref so the tracker does not rebuild every callback on each roster
 *   change (the lookup only runs inside event handlers).
 * - `spendTurnRef`: the advisory action-economy spender (v8.309.0), a ref
 *   because the handlers that charge a turn are created before the tracker.
 * - `actionEconomy`: the readout (and `endSuggested` from it) - null when the rule is off, the tracker is
 *   not running, or the actor is not in the order (pips for a fight nobody
 *   is tracking are noise) - and its manual reset.
 *
 * Extracted from DeathWatchSimulator.
 */
import { effectiveSheet } from '../engine/roster/stancedSheet';
import { useState, useRef, useMemo, useCallback } from 'react';
import * as settings from '../engine/settings/resolve';
import { economyOf, pointsFor, turnSpent } from '../engine/actions/economy';
import useTurnTracker from './useTurnTracker';

/** The actor's action economy, or null when there is no tracked turn to spend from. */
export function actionEconomyOf(showActionEconomy, trackerState, actor) {
  if (!showActionEconomy || !trackerState?.active || !actor) return null;
  const p = trackerState.participants.find((x) => x.combatantId === actor.id);
  return p ? economyOf(p.actionPoints) : null;
}

/** The table's initiative rulings, as the tracker takes them. */
function initiativeRules(permissions) {
  return {
    autoResolveTurnEffects: settings.resolveAutoResolveTurnEffects(permissions),
    autoRollInitiative: settings.resolveAutoRollInitiative(permissions),
    initiativeArrivals: settings.resolveInitiativeArrivals(permissions),
    initiativeTieBreak: settings.resolveInitiativeTieBreak(permissions),
    rerollEachRound: settings.resolveRerollEachRound(permissions),
    rerollKeepsManual: settings.resolveRerollKeepsManual(permissions),
    skipDownedTurns: settings.resolveSkipDownedTurns(permissions),
  };
}

/**
 * The sheet a combatant ROLLS with, read from the latest roster: the effective one, wearing the
 * stances that are up (a stance's Initiative bonus counts). For reading only - never written back.
 */
function useSheetFor(roster) {
  const rosterRef = useRef(roster);
  rosterRef.current = roster;
  return useCallback((combatantId) => {
    const c = rosterRef.current.find((x) => x.id === combatantId);
    return c ? effectiveSheet(c) : null;
  }, []);
}

/**
 * The action economy's spender for every press - drum, strip, move: it charges only while it is that combatant's
 * turn (the user, 2026-10-28). Pips reset per round, so a press made for him off his turn - the GM working a brother's
 * strip - would otherwise eat his own turn's pips.
 */
export const chargeOnHisTurn = (tracker) => (combatantId, points) => {
  if (tracker.isTabActive?.(combatantId)) tracker.spendActionPoints(combatantId, points);
};

/** The start-of-turn prompt queue and its answer. */
export function useTurnEffectPrompts(turnTracker, [turnEffectQueue, setTurnEffectQueue]) {
  return useCallback(
    (choice) => {
      const req = turnEffectQueue[0];
      setTurnEffectQueue((q) => q.slice(1));
      if (!req || choice === 'skip') return;
      turnTracker.resolveTurnEffectTest(req, choice === 'assist' ? (req.rule.endsOn?.assistBonus ?? 0) : 0);
      // Pinned's shake-off is his Full Action (2026-10-26): charged once the test is rolled - and only while it is
      // still his turn: a prompt answered after it ended would charge a turn the test was not taken in (the review).
      const his = turnTracker.isTabActive?.(req.combatantId);
      if (req.rule.actionCost && his) turnTracker.spendActionPoints(req.combatantId, pointsFor(req.rule.actionCost));
    },
    [turnEffectQueue, turnTracker, setTurnEffectQueue],
  );
}

/** `useTurnTracker`, with the shell's sync, writers and rulings. */
function useTrackerCore(args, onTurnEffectTests, sheetFor) {
  const { sync } = args;
  return useTurnTracker({
    ...initiativeRules(args.permissions),
    tabs: args.tabs,
    isLocked: args.isLocked,
    turnTrackerState: sync.turnTrackerState,
    setTurnTrackerState: sync.setTurnTrackerState,
    syncTurnTrackerChange: sync.syncTurnTrackerChange,
    // The whole-state replace, for a scene cut's imposed order (v8.337.0).
    syncTurnTrackerToFirestore: sync.syncTurnTrackerToFirestore,
    capturePreActionSnapshot: args.capturePreActionSnapshot,
    historyTrack: args.historyTrack,
    updateTab: args.updateTab,
    addNarrative: args.addNarrative,
    roundCountdown: args.roundCountdown,
    stackPolicy: args.stackPolicy,
    protagonistRules: args.protagonistRules,
    unnaturalDosMode: args.unnaturalDosMode,
    onTurnEffectTests,
    onTurnStart: args.onTurnStart,
    sheetFor,
    initiativeModFor: args.initiativeModFor,
    // A hidden figure is never admitted to the shared order (ADR 2026-10-07).
    isHidden: args.hidden?.isHidden,
    isGm: !!args.isGm,
  });
}

/**
 * @param {object} args - tabs, roster, actor, isLocked, sync (the Firestore
 *   sync record), permissions, capturePreActionSnapshot, historyTrack,
 *   updateTab, addNarrative, roundCountdown, stackPolicy, protagonistRules,
 *   unnaturalDosMode, initiativeModFor, showActionEconomy.
 */
export default function useShellTurnTracker(args) {
  const { actor, showActionEconomy } = args;
  const queue = useState([]);
  const sheetForCombatant = useSheetFor(args.roster);
  const turnTracker = useTrackerCore(args, queue[1], sheetForCombatant);
  const spendTurnRef = useRef(null);
  spendTurnRef.current = chargeOnHisTurn(turnTracker);
  const resetActionEconomy = useCallback(() => {
    if (actor) turnTracker.resetActionPoints(actor.id);
  }, [actor, turnTracker]);
  const actionEconomy = useMemo(
    () => actionEconomyOf(showActionEconomy, turnTracker.trackerState, actor),
    [showActionEconomy, turnTracker.trackerState, actor],
  );
  const handleTurnEffectChoice = useTurnEffectPrompts(turnTracker, queue);
  const pendingTurnEffect = queue[0][0] ?? null;
  return {
    turnTracker,
    sheetForCombatant,
    spendTurnRef,
    resetActionEconomy,
    actionEconomy,
    // THE SUGGESTED END: both END TURN buttons light when the actor's pips
    // are spent - or overspent. Advisory, as the pips are: never an auto end.
    endSuggested: !!actionEconomy && turnSpent(actionEconomy),
    pendingTurnEffect,
    handleTurnEffectChoice,
  };
}
